/**
 * Inline SVG icon vocabulary for the admin shell (spec C4, 2026-07-09 wave).
 *
 * Design contract (docs/DESIGN-PHILOSOPHY.md):
 *  - 18px stroke icons, `currentColor` only — they inherit the text color of
 *    whatever chrome they sit in (sidebar link, rail, topbar button), so the
 *    two-layer token system stays intact (no color decisions here).
 *  - No icon library dependency: every glyph is a hand-rolled 24×24 stroke
 *    path. Decoration that doesn't carry information is out (Principle 3);
 *    each glyph exists to make the icon rail and drawer scannable one-handed.
 *  - Icons are always `aria-hidden` — the accessible name lives on the link/
 *    button that renders them (label text, `aria-label`, or `title`).
 *
 * The `NavIconName` union is owned by `lib/route-permissions.ts` (the nav
 * items carry their icon key there); the `Record<IconName, …>` below is
 * exhaustiveness-checked against it, so adding a nav item without a glyph is
 * a compile error, not a silently blank slot.
 */
import type { NavIconName } from '../lib/route-permissions.js';

/** Shell-chrome glyphs that aren't tied to a nav item. */
export type UiIconName =
  | 'menu'
  | 'search'
  | 'help' // Sawyer 17 ai-manager-help (F5)
  | 'plus'
  | 'close'
  | 'chevronDown'
  | 'signOut'
  | 'print';

export type IconName = NavIconName | UiIconName;

/**
 * 24×24 stroke path content per glyph. Stroke attributes (width, caps,
 * color) live once on the shared <svg> in <Icon />.
 */
const ICON_PATHS: Record<IconName, JSX.Element> = {
  // ── Nav glyphs ──────────────────────────────────────────────────────
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  // #548 — Front-Desk: a concierge bell (the front-desk universal).
  frontDesk: (
    <>
      <path d="M3 20h18" />
      <path d="M4 20a8 8 0 0 1 16 0" />
      <path d="M12 8V6" />
      <path d="M10 6h4" />
    </>
  ),
  timeclock: (
    <>
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l2.5 2.5" />
      <path d="M9 3h6" />
    </>
  ),
  // #427 W2 — /my-schedule: a calendar with a person in it (your own week).
  mySchedule: (
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18" />
      <path d="M8 2v4M16 2v4" />
      <circle cx="12" cy="14" r="2" />
      <path d="M8.5 18.5a3.6 3.6 0 017 0" />
    </>
  ),
  timeclockManage: (
    <>
      <rect x="4" y="4" width="16" height="17" rx="2" />
      <path d="M8 2v4M16 2v4" />
      <circle cx="12" cy="14" r="4.5" />
      <path d="M12 11.8v2.2l1.6 1.6" />
    </>
  ),
  bookings: (
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18" />
      <path d="M8 2v4M16 2v4" />
      <path d="M9 15l2 2 4-4" />
    </>
  ),
  // Wave 5 (#940 Part A) — /calendar/day: a calendar page with list lines.
  dayList: (
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18" />
      <path d="M8 2v4M16 2v4" />
      <path d="M7 13h10M7 17h6" />
    </>
  ),
  schedule: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </>
  ),
  incidents: (
    <>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </>
  ),
  compliance: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  // #551 — an open envelope with a check: records arriving by email that
  // still need someone to sign off. Deliberately NOT the compliance shield
  // (that's the roster) and NOT a plain envelope (that's messaging).
  vaxInbox: (
    <>
      <path d="M3 8l9 6 9-6" />
      <path d="M21 8v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8l9-5 9 5z" />
      <path d="M9 13l2 2 4-4" />
    </>
  ),
  customers: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  staff: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <path d="M20 8v6" />
      <path d="M17 11h6" />
    </>
  ),
  payments: (
    <>
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <path d="M1 10h22" />
    </>
  ),
  loyalty: (
    <>
      <path d="M20 12v10H4V12" />
      <rect x="2" y="7" width="20" height="5" rx="1" />
      <path d="M12 22V7" />
      <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
      <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </>
  ),
  // #814 — pricing rules: a percent mark (discount/surcharge rules).
  pricingRules: (
    <>
      <path d="M19 5L5 19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </>
  ),
  reports: (
    <>
      <path d="M12 20v-10" />
      <path d="M18 20V4" />
      <path d="M6 20v-4" />
    </>
  ),
  // #427 W3 — Timesheets & Payroll: a document with hour rows + a clock face.
  timesheets: (
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6" />
      <path d="M8 8h6M8 12h3" />
      <circle cx="17" cy="15" r="4.5" />
      <path d="M17 13v2l1.4 1.4" />
    </>
  ),
  aiManager: (
    <>
      <path d="M12 3l1.9 4.6L18.5 9.5l-4.6 1.9L12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3z" />
      <path d="M19 15l.9 2.1 2.1.9-2.1.9-.9 2.1-.9-2.1-2.1-.9 2.1-.9L19 15z" />
    </>
  ),
  conversations: (
    <>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </>
  ),
  // Sawyer 16 staff-messaging (E4) — Team messages: two overlapping speech bubbles.
  teamMessages: (
    <>
      <path d="M14 9a2 2 0 0 1-2 2H6l-4 3V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2z" />
      <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-3h-6a2 2 0 0 1-2-2v-1" />
    </>
  ),
  walker: (
    <>
      <circle cx="7" cy="7.5" r="1.6" />
      <circle cx="12" cy="5.5" r="1.6" />
      <circle cx="17" cy="7.5" r="1.6" />
      <path d="M12 11c-3.1 0-5.5 2.3-5.5 5.1 0 1.6 1.3 2.9 2.9 2.9h5.2c1.6 0 2.9-1.3 2.9-2.9 0-2.8-2.4-5.1-5.5-5.1z" />
    </>
  ),
  kennel: (
    <>
      <path d="M3 10.5L12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
      <path d="M10 21v-5h4v5" />
    </>
  ),
  // Sawyer 08 meds-feeding (A2) — the med pass: a capsule.
  medPass: (
    <>
      <rect x="3.5" y="8.5" width="17" height="7" rx="3.5" transform="rotate(-35 12 12)" />
      <path d="M9.6 8.6l4.8 6.8" />
    </>
  ),
  // #939 3b — the Lodging Calendar: a bed.
  lodging: (
    <>
      <path d="M3 18V8" />
      <path d="M3 13h18v5" />
      <path d="M3 13V9a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v4" />
      <path d="M12 13V11a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v2" />
    </>
  ),
  groomer: (
    <>
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <path d="M20 4L8.12 15.88" />
      <path d="M14.47 14.48L20 20" />
      <path d="M8.12 8.12L12 12" />
    </>
  ),
  accountant: (
    <>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M8 6h8" />
      <path d="M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01M8 19h.01M12 19h.01M16 19h.01" />
    </>
  ),
  // #933 — settings: three sliders (the owner's dials).
  settings: (
    <>
      <path d="M4 7h10" />
      <path d="M18 7h2" />
      <circle cx="16" cy="7" r="2" />
      <path d="M4 17h4" />
      <path d="M12 17h8" />
      <circle cx="10" cy="17" r="2" />
    </>
  ),
  audit: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </>
  ),
  tenants: (
    <>
      <path d="M4 22V4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v18" />
      <path d="M2 22h20" />
      <path d="M9 6h.01M15 6h.01M9 10h.01M15 10h.01M9 14h.01M15 14h.01" />
      <path d="M10 22v-4h4v4" />
    </>
  ),
  tenantNew: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M12 8v8" />
      <path d="M8 12h8" />
    </>
  ),
  intakes: (
    <>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>
  ),

  // ── Shell-chrome glyphs ─────────────────────────────────────────────
  menu: (
    <>
      <path d="M3 6h18" />
      <path d="M3 12h18" />
      <path d="M3 18h18" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </>
  ),
  // Sawyer 17 ai-manager-help (F5) — circled question mark for the top-bar Help.
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.25a2.5 2.5 0 0 1 4.86.83c0 1.67-2.36 2.17-2.36 3.67" />
      <path d="M12 17h.01" />
    </>
  ),
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  close: (
    <>
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </>
  ),
  chevronDown: (
    <>
      <path d="M6 9l6 6 6-6" />
    </>
  ),
  signOut: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),
  // #564 — printer: the morning-board print action.
  print: (
    <>
      <path d="M6 9V3h12v6" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="7" rx="1" />
    </>
  ),
};

type IconProps = {
  name: IconName;
  /** Rendered box size in px. 18 is the shell standard (spec C4). */
  size?: number;
  className?: string;
};

/**
 * Shared stroke icon. Always decorative (`aria-hidden`) — the parent
 * link/button owns the accessible name.
 */
export function Icon({ name, size = 18, className }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      data-icon={name}
    >
      {ICON_PATHS[name]}
    </svg>
  );
}
